import React, { useState } from "react";

/* ─────────────────────────────────────────────
   ALL styles self-contained — no external CSS,
   no Bootstrap classes, no CKYC.css import.
   Theme: #438c3d / #66bb6a
───────────────────────────────────────────── */

const fontFaceCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');

@keyframes ckyc-pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:0.4; transform:scale(0.84); }
}
@keyframes ckyc-fadein {
  from { opacity:0; transform:translateY(10px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes ckyc-spin-in {
  from { opacity:0; transform:scale(0.88) translateY(8px); }
  to   { opacity:1; transform:scale(1)    translateY(0); }
}
@keyframes ckyc-spin {
  to { transform: rotate(360deg); }
}
`;

const S = {
  primary: "#438c3d",
  primaryDark: "#2f6629",
  primaryLight: "#66bb6a",
  primaryPale: "#eaf4e9",
  primaryGlass: "rgba(67,140,61,0.09)",
  surface: "#ffffff",
  surface2: "#f7fbf7",
  surface3: "#f0f8ef",
  textPrimary: "#1e3b1d",
  textSec: "#4a6649",
  textMuted: "#7a9578",
  border: "rgba(67,140,61,0.18)",
  borderStrong: "rgba(67,140,61,0.35)",
  danger: "#c0392b",
  dangerBg: "#fdecea",
  fontDisplay: "'DM Serif Display',Georgia,serif",
  fontBody: "'DM Sans',system-ui,-apple-system,sans-serif",
  radiusMd: "12px",
  radiusLg: "20px",
  radiusXl: "30px",
  shadowLg: "0 24px 80px rgba(11,28,10,0.30),0 6px 20px rgba(0,0,0,0.10)",
};

const styles = {
  /* Backdrop */
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1055,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px 14px",
    background:
      "radial-gradient(circle at 18% 14%,rgba(67,140,61,0.22),transparent 22%),radial-gradient(circle at 85% 10%,rgba(20,100,80,0.18),transparent 24%),linear-gradient(180deg,rgba(10,20,10,0.62),rgba(10,20,10,0.66))",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    fontFamily: S.fontBody,
    animation: "ckyc-fadein 0.22s ease",
  },

  /* Card */
  card: {
    background: S.surface,
    borderRadius: S.radiusXl,
    overflow: "hidden",
    boxShadow: S.shadowLg,
    width: "100%",
    maxWidth: "440px",
    position: "relative",
    animation: "ckyc-spin-in 0.26s cubic-bezier(0.34,1.56,0.64,1)",
    maxheight: "92vh",
  },

  /* Left accent stripe */
  stripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
    background: `linear-gradient(180deg,${S.primaryLight},${S.primary})`,
    borderRadius: "4px 0 0 4px",
    zIndex: 2,
    pointerEvents: "none",
  },

  /* Hero strip */
  hero: {
    padding: "15px 28px 10px",
    background: `linear-gradient(135deg,${S.primaryDark} 0%,#1a5c38 50%,${S.primary} 100%)`,
    position: "relative",
    overflow: "hidden",
  },
  heroGlow1: {
    position: "absolute",
    width: "180px",
    height: "180px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.07)",
    top: "-70px",
    right: "-50px",
    pointerEvents: "none",
  },
  heroGlow2: {
    position: "absolute",
    width: "90px",
    height: "90px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    bottom: "-24px",
    left: "38%",
    pointerEvents: "none",
  },
  heroBadge: {
    display: "inline-block",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.13em",
    textTransform: "uppercase",
    color: S.primaryLight,
    marginBottom: "4px",
    position: "relative",
    zIndex: 1,
  },
  heroTitle: {
    fontFamily: S.fontDisplay,
    fontSize: "22px",
    fontWeight: 400,
    color: "#fff",
    margin: "0 0 6px",
    lineHeight: 1.2,
    position: "relative",
    zIndex: 1,
  },
  heroSub: {
    fontSize: "13px",
    color: "rgba(255,255,255,0.70)",
    margin: 0,
    lineHeight: 1.5,
    position: "relative",
    zIndex: 1,
  },

  /* Header row (close btn lives here) */
  headRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px 12px 28px",
    borderBottom: `1px solid ${S.border}`,
    background: S.surface2,
    gap: "12px",
  },
  headTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: S.textPrimary,
  },
  headSub: {
    fontSize: "11.5px",
    color: S.textMuted,
    marginTop: "1px",
  },
  closeBtn: (hovered) => ({
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    background: hovered ? S.dangerBg : S.surface3,
    border: `1px solid ${hovered ? "rgba(192,57,43,0.30)" : S.border}`,
    color: hovered ? S.danger : S.textSec,
    fontSize: "16px",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
    transition: "all 0.2s",
  }),

  /* Body */
  body: {
    padding: "20px 28px 30px",
    background: `linear-gradient(180deg,${S.surface} 0%,${S.surface2} 100%)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "16px",
  },

  /* KYC illustration wrapper */
  imgWrap: {
    width: "96px",
    height: "96px",
    borderRadius: "50%",
    background: S.primaryPale,
    border: `2px solid ${S.borderStrong}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  img: {
    width: "68px",
    height: "68px",
    objectFit: "contain",
  },

  /* Status text */
  statusTitle: {
    fontFamily: S.fontDisplay,
    fontSize: "20px",
    fontWeight: 400,
    color: S.textPrimary,
    margin: 0,
    lineHeight: 1.25,
  },
  statusDesc: {
    fontSize: "13.5px",
    color: S.textSec,
    lineHeight: 1.6,
    margin: 0,
  },

  /* Divider */
  divider: {
    width: "100%",
    height: "1px",
    background: S.border,
    margin: "2px 0",
  },

  /* Primary CTA button */
  primaryBtn: (hovered) => ({
    fontFamily: S.fontBody,
    fontSize: "14px",
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: "#fff",
    background: `linear-gradient(135deg,${S.primaryLight} 0%,${S.primary} 100%)`,
    border: "none",
    borderRadius: S.radiusMd,
    padding: "11px 32px",
    cursor: "pointer",
    boxShadow: hovered
      ? "0 6px 20px rgba(67,140,61,0.45)"
      : "0 2px 12px rgba(67,140,61,0.32),0 1px 3px rgba(0,0,0,0.08)",
    transform: hovered ? "translateY(-1px)" : "none",
    filter: hovered ? "brightness(1.08)" : "none",
    transition: "all 0.22s",
    whiteSpace: "nowrap",
  }),

  /* Success badge shown after redirect */
  successBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: 500,
    color: S.primary,
    background: S.primaryPale,
    border: `1px solid ${S.borderStrong}`,
    borderRadius: "999px",
    padding: "5px 14px",
  },
  successDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: S.primaryLight,
    animation: "ckyc-pulse 2.4s ease-in-out infinite",
    flexShrink: 0,
  },
};

/* ── Tiny hover hook ── */
function useHover() {
  const [hovered, set] = useState(false);
  return [
    hovered,
    { onMouseEnter: () => set(true), onMouseLeave: () => set(false) },
  ];
}

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const VeryficationCKYC = (props) => {
  const [IsRedirectionSuccess, setRedirectionSuccess] = useState(false);
  const [closeHover, closeHoverEvts] = useHover();
  const [primaryHover, primaryHoverEvts] = useHover();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setRedirection = () => {
    window.open(props.URL, "_blank");
    setRedirectionSuccess(true);
  };

  const getCKYCData = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await props.onKYCSubmit({
        IsRedirectUrl: true,
        IsUploadFile: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{fontFaceCSS}</style>

      {/* Backdrop */}
      <div
        style={styles.backdrop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ckycVerifyTitle"
      >
        {/* Card */}
        <div style={styles.card}>
          {/* Accent stripe */}
          <div style={styles.stripe} />

          {/* ── Hero ── */}
          <div style={styles.hero}>
            <div style={styles.heroGlow1} />
            <div style={styles.heroGlow2} />
            <div style={styles.heroBadge}>KYC Verification</div>
            <h1 style={styles.heroTitle} id="ckycVerifyTitle">
              No KYC Details Found!
            </h1>
            {/* <p style={styles.heroSub}>
              Complete your verification to proceed securely.
            </p> */}
          </div>

          {/* ── Sub-header ── */}
          <div style={styles.headRow}>
            <div>
              <div style={styles.headTitle}>Identity Verification</div>
              <div style={styles.headSub}>
                Complete KYC to unlock your policy.
              </div>
            </div>
            <button
              type="button"
              style={styles.closeBtn(closeHover)}
              onClick={() => props.closeKYC(false)}
              aria-label="Close"
              {...closeHoverEvts}
            >
              ×
            </button>
          </div>

          {/* ── Body ── */}
          <div style={styles.body}>
            {/* Illustration */}
            <div style={styles.imgWrap}>
              <img
                src={require("../Images/verify-me.webp")}
                alt="KYC verification"
                style={styles.img}
              />
            </div>

            {/* Status copy */}
            <p style={styles.statusTitle}>
              {IsRedirectionSuccess
                ? "Verification in Progress"
                : "Complete Your KYC"}
            </p>
            <p style={styles.statusDesc}>
              {IsRedirectionSuccess
                ? "You've been redirected to complete verification. Click Submit once done."
                : "You need to verify your identity before we can proceed with your policy."}
            </p>

            {/* Success badge — shown after redirect opens */}
            {IsRedirectionSuccess && (
              <div style={styles.successBadge}>
                <span style={styles.successDot} />
                Redirected — complete steps in the new tab
              </div>
            )}

            <div style={styles.divider} />

            {/* CTA */}
            {IsRedirectionSuccess ? (
              <button
                onClick={getCKYCData}
                style={styles.primaryBtn(primaryHover)}
                disabled={isSubmitting}
                {...primaryHoverEvts}
              >
                {isSubmitting ? (
                  <>
                    Submitting...
                    <span
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.35)",
                        borderTopColor: "#fff",
                        animation: "ckyc-spin 0.75s linear infinite",
                        display: "inline-block",
                      }}
                    />
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            ) : (
              <button
                onClick={setRedirection}
                style={styles.primaryBtn(primaryHover)}
                {...primaryHoverEvts}
              >
                Verify My Identity
              </button>
            )}
          </div>
          {/* /body */}
        </div>
        {/* /card */}
      </div>
      {/* /backdrop */}
    </>
  );
};

export default VeryficationCKYC;
